import type { Schema } from '@dealdesk/api-schema';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/design-system';
import type { Disposition, DownloadLink } from '@/shared/files';
import { formatDateTime } from '@/shared/lib/format';

import { StatusBadge } from '../status';
import { FileActions } from './FileActions';
import { fileSize } from './messages';

export interface VersionHistoryProps {
  versions: readonly Schema<'DocumentVersion'>[] | undefined;
  loading?: boolean;
  error?: string | null;
  /** Link to one version (the owning feature's api function). */
  fetchVersionLink: (versionId: string, disposition: Disposition) => Promise<DownloadLink>;
}

/** Every uploaded version of a document, newest first: who and when, scan state, review outcome, download if clean. */
export function VersionHistory({ versions, loading, error, fetchVersionLink }: VersionHistoryProps) {
  const { t } = useTranslation();
  if (loading) return <Skeleton className="h-10 w-full" />;
  if (error)
    return (
      <p className="text-meta text-negative" role="alert">
        {error}
      </p>
    );
  if (!versions?.length) return <p className="text-meta text-ink-muted">{t('files.versions.empty')}</p>;
  return (
    <ol className="flex flex-col" aria-label={t('files.versions.title')}>
      {versions.map((v) => (
        <li key={v.id} className="flex min-w-0 items-center gap-3 border-b border-border py-1.5 last:border-b-0">
          <span className="w-16 shrink-0 text-meta text-ink-secondary">{t('files.versions.version', { n: v.versionNo })}</span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-body text-ink" title={v.fileName} dir="auto">
                {v.fileName}
              </span>
              {v.current ? <span className="shrink-0 text-meta font-medium text-ink-secondary">{t('files.versions.current')}</span> : null}
            </span>
            <span className="truncate text-meta text-ink-muted">
              {[
                v.size ? fileSize(t, v.size) : null,
                v.uploadedBy ? t('files.versions.uploadedBy', { name: v.uploadedBy.name, time: formatDateTime(v.uploadedAt) }) : formatDateTime(v.uploadedAt),
                v.review ? t(`files.versions.${v.review.decision}`, { name: v.review.by?.name ?? '' }) : null,
                v.review?.note ?? null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
          {v.scanStatus !== 'clean' ? <StatusBadge registry="fileScanStatus" value={v.scanStatus} /> : null}
          <FileActions fileName={v.fileName} contentType={v.contentType ?? null} scanStatus={v.scanStatus} fetchLink={(d) => fetchVersionLink(v.id, d)} />
        </li>
      ))}
    </ol>
  );
}
